import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ScanAmbience } from "./Ambience";

const steps = [
  {
    n: "01",
    title: "Analyse & brief stratégique",
    duration: "1 — 2 jours",
    text: "Nous prenons le temps de comprendre votre activité, vos objectifs et votre cible pour créer un site qui répond précisément à vos besoins.",
    deliverables: ["Brief validé", "Arborescence du site", "Recommandations stratégiques"],
  },
  {
    n: "02",
    title: "Design & maquettes interactives",
    duration: "3 — 5 jours",
    text: "Création de maquettes modernes et interactives. Vous validez le design avant tout développement : zéro surprise, 100% de contrôle.",
    deliverables: ["Maquettes desktop & mobile", "Charte graphique", "Validation avant code"],
  },
  {
    n: "03",
    title: "Développement & optimisation",
    duration: "1 — 3 semaines",
    text: "Code propre, performant et optimisé SEO. Technologies modernes pour un site rapide, sécurisé et évolutif.",
    deliverables: ["Site en pré-production", "Optimisations SEO intégrées", "Tests sur tous supports"],
  },
  {
    n: "04",
    title: "Mise en ligne & accompagnement",
    duration: "Suivi 30 jours offert",
    text: "Formation personnalisée, mise en ligne sécurisée et suivi post-lancement pour assurer votre réussite à long terme.",
    deliverables: ["Site en ligne et opérationnel", "Formation à la gestion", "Support technique 30 jours"],
  },
];

function Step({ step, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 75%", "start 30%"],
  });
  const opacity = useTransform(scrollYProgress, [0, 1], [0.2, 1]);

  return (
    <motion.article
      ref={ref}
      data-testid={`method-step-${step.n}`}
      style={{ opacity, position: "relative" }}
      className="border-t border-white/[0.08] py-12 sm:py-16 grid grid-cols-12 gap-6 sm:gap-10"
    >
      <div className="col-span-3 sm:col-span-2">
        <span className="font-display text-4xl sm:text-5xl tracking-tighter text-[var(--accent)]">
          {step.n}
        </span>
      </div>
      <div className="col-span-9 sm:col-span-7">
        <h3 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight mb-3">
          {step.title}
        </h3>
        <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-6 max-w-xl">
          {step.text}
        </p>
        <ul className="space-y-1.5">
          {step.deliverables.map((d) => (
            <li key={d} className="flex items-center gap-2 text-sm text-zinc-300 font-mono">
              <span className="text-[var(--accent)]">→</span>
              {d}
            </li>
          ))}
        </ul>
      </div>
      <div className="col-span-12 sm:col-span-3 flex sm:justify-end">
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 sm:text-right">
          {step.duration}
        </span>
      </div>
    </motion.article>
  );
}

export default function Method() {
  return (
    <section
      id="methode"
      data-testid="method-section"
      className="relative overflow-hidden py-28 sm:py-40 px-5 sm:px-8 lg:px-12"
    >
      <ScanAmbience />
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6 block">
              (03) — Méthode
            </span>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter mb-6">
              Notre <br />
              <span className="text-outline">méthode</span>
            </h2>
            <p className="text-zinc-400 max-w-sm leading-relaxed">
              Un processus clair et transparent, du brief initial à la mise en ligne. À chaque étape, vous savez où nous en sommes — et où vous allez.
            </p>
          </div>
        </div>

        <div className="lg:col-span-8">
          {steps.map((s, i) => (
            <Step key={s.n} step={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
